import { rootAuthLoader } from "@clerk/react-router/server";
import { posthog } from "posthog-js";
import { useEffect } from "react";
import * as ReactRouter from "react-router";
import type { Route } from "../.react-router/types/app/+types/root";
import { PublicCrispChat } from "./components/crisp-chat";
import { ErrorPage } from "./components/error-page";
import { NavigationProgress } from "./components/navigation-progress";
import { PosthogInit } from "./components/posthog";
import { Toaster } from "./components/ui/toaster";
import { getCanonicalHostRedirectUrl } from "./lib/canonical-host.server";
import {
  DATA_LOCALE_HANDLE,
  getLocaleFromRequest,
  type Locale,
  resolveLocaleFromMatches,
  setCurrentLocale,
} from "./lib/locale";
import { capturePostHogException } from "./lib/posthog";
import stylesheet from "./tailwind.css?url";

export const meta: ReactRouter.MetaFunction = () => [{ title: "AnyLawyer" }];

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap",
  },
];

type RootLoaderData = {
  locale: Locale;
  config: {
    PARROT_ENV: typeof process.env.PARROT_ENV;
  };
};

export const handle = DATA_LOCALE_HANDLE;
export const middleware: Route.MiddlewareFunction[] = [localeMiddleware];

// Some server loaders/actions call t() before the root component renders,
// so we seed the request locale here to keep those translations correct.
// biome-ignore lint/suspicious/noExplicitAny: middleware args are not fully typed for reusable functions
async function localeMiddleware(args: any, next: any) {
  setCurrentLocale(getLocaleFromRequest(args.request));
  return next();
}

export async function loader(args: Route.LoaderArgs) {
  const canonicalHostRedirectUrl = getCanonicalHostRedirectUrl(args.request);
  if (canonicalHostRedirectUrl) {
    throw ReactRouter.redirect(canonicalHostRedirectUrl, 301);
  }

  const pathname = new URL(args.request.url).pathname;
  const locale = getLocaleFromRequest(args.request);
  const baseLoaderData: RootLoaderData = {
    locale,
    config: {
      PARROT_ENV: process.env.PARROT_ENV,
    },
  };

  if (
    pathname.startsWith("/app") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/organization")
  ) {
    return rootAuthLoader(args, async () => baseLoaderData);
  }

  return baseLoaderData;
}

export default function App({ loaderData }: Route.ComponentProps) {
  const location = ReactRouter.useLocation();
  const matches = ReactRouter.useMatches();
  const locale = resolveLocaleFromMatches(matches, loaderData?.locale);

  setCurrentLocale(locale);

  useEffect(() => {
    posthog.capture("$pageview");
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const isWordAddinRoute = location.pathname.startsWith("/word/");
  const appContent = (
    <>
      <ReactRouter.Outlet />
      <Toaster />
      {isWordAddinRoute ? null : <ReactRouter.ScrollRestoration />}
      <ReactRouter.Scripts />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.PARROT_CONFIG = ${JSON.stringify(loaderData?.config ?? {})};`,
        }}
      />
    </>
  );

  return (
    <html lang={locale}>
      <head>
        <ReactRouter.Meta />
        <ReactRouter.Links />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
      </head>
      <body>
        {isWordAddinRoute ? null : <NavigationProgress />}
        {appContent}
        <PublicCrispChat />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error, loaderData }: Route.ErrorBoundaryProps) {
  const matches = ReactRouter.useMatches();
  const is404 = ReactRouter.isRouteErrorResponse(error) && error.status === 404;
  const locale = resolveLocaleFromMatches(matches, loaderData?.locale);

  setCurrentLocale(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    if (is404) return;
    if (!(error instanceof Error)) return;

    // Let browser-level exception observers see handled React render errors.
    // This preserves the standard exception pipeline that PostHog uses for
    // source-map resolution. Fall back to manual capture when unavailable.
    if (typeof window.reportError === "function") {
      const timeoutId = window.setTimeout(() => {
        window.reportError(error);
      }, 0);

      return () => window.clearTimeout(timeoutId);
    }

    capturePostHogException(error, {
      source: "root-error-boundary",
      capture_method: "manual-fallback",
    });
  }, [error, is404]);
  const stack =
    import.meta.env.DEV && !is404 && error instanceof Error
      ? error.stack
      : undefined;

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <ReactRouter.Meta />
        <ReactRouter.Links />
      </head>
      <body>
        <PosthogInit />
        <ErrorPage is404={is404} stack={stack} locale={locale} />
        <ReactRouter.ScrollRestoration />
        <ReactRouter.Scripts />
      </body>
    </html>
  );
}
